import React, { useState } from "react";
import { buildApiUrl } from "../helpers/base_url";
const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus("idle");

    try {
      const response = await fetch(buildApiUrl("/api/contact-messages"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          fullName: formData.name,
          email: formData.email,
          phoneNumber: formData.phone,
          subject: formData.subject,
          message: formData.message,
        }),
      });

      if (response.ok) {
        setSubmitStatus("success");
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      } else {
        setSubmitStatus("error");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section
        style={{
          background:
            "linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)",
          padding: "120px 0 80px 0",
          textAlign: "center",
          color: "white",
          marginTop: 64,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Animated background patterns */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
            radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(255,255,255,0.05) 0%, transparent 50%)
          `,
            animation: "float 6s ease-in-out infinite",
          }}
        />

        {/* Floating decorative elements */}
        <div
          style={{
            position: "absolute",
            top: "20%",
            left: "10%",
            width: "60px",
            height: "60px",
            background: "rgba(255,255,255,0.1)",
            borderRadius: "50%",
            animation: "bounce 3s ease-in-out infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "60%",
            right: "15%",
            width: "40px",
            height: "40px",
            background: "rgba(255,255,255,0.15)",
            borderRadius: "50%",
            animation: "bounce 4s ease-in-out infinite 1s",
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "30%",
            right: "25%",
            width: "30px",
            height: "30px",
            background: "rgba(255,255,255,0.1)",
            borderRadius: "50%",
            animation: "bounce 5s ease-in-out infinite 2s",
          }}
        />

        {/* Geometric shapes */}
        <div
          style={{
            position: "absolute",
            top: "10%",
            right: "5%",
            width: "80px",
            height: "80px",
            border: "2px solid rgba(255,255,255,0.2)",
            borderRadius: "50%",
            animation: "rotate 20s linear infinite",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: "20%",
            left: "5%",
            width: "100px",
            height: "100px",
            border: "2px solid rgba(255,255,255,0.15)",
            transform: "rotate(45deg)",
            animation: "rotate 25s linear infinite reverse",
          }}
        />

        {/* Wave effect at bottom */}
        <div
          style={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            height: "60px",
            background:
              "linear-gradient(45deg, transparent 33.333%, rgba(255,255,255,0.1) 33.333%, rgba(255,255,255,0.1) 66.667%, transparent 66.667%)",
            backgroundSize: "30px 30px",
            animation: "wave 3s ease-in-out infinite",
          }}
        />

        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 20px",
            position: "relative",
            zIndex: 2,
          }}
        >
          <div
            style={{
              display: "inline-block",
              background: "rgba(255,255,255,0.1)",
              padding: "20px 40px",
              borderRadius: "50px",
              marginBottom: "30px",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.2)",
            }}
          >
            <span
              style={{
                fontSize: 18,
                fontWeight: 600,
                color: "#ffffff",
                textShadow: "0 2px 4px rgba(0,0,0,0.2)",
              }}
            >
              Let's Connect
            </span>
          </div>

          <h1
            style={{
              fontSize: 48,
              fontWeight: 800,
              marginBottom: 20,
              color: "#ffffff",
              textShadow:
                "0 4px 12px rgba(0,0,0,0.4), 0 2px 4px rgba(0,0,0,0.3), 0 0 0 rgba(0,0,0,0.2)",
              letterSpacing: "-0.5px",
            }}
          >
            Get In Touch
          </h1>

          <p
            style={{
              fontSize: 20,
              maxWidth: 600,
              margin: "0 auto",
              lineHeight: 1.7,
              color: "#ffffff",
              textShadow:
                "0 2px 8px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)",
              fontWeight: 400,
            }}
          >
            Have questions about our services? We'd love to hear from you. Send
            us a message and we'll respond as soon as possible.
          </p>

          {/* Contact quick info */}
          <div
            className="contact-quick-info"
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "24px",
              marginTop: "50px",
              flexWrap: "wrap",
            }}
          >
            <a
              href="tel:+2349054056702"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "14px",
                background: "rgba(255,255,255,0.95)",
                padding: "16px 28px",
                borderRadius: "16px",
                backdropFilter: "blur(20px)",
                border: "2px solid rgba(255,255,255,0.3)",
                boxShadow:
                  "0 8px 32px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.05)",
                textDecoration: "none",
                color: "#333",
                fontWeight: 600,
                fontSize: 16,
                transition: "all 0.3s ease",
                cursor: "pointer",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow =
                  "0 12px 40px rgba(0,0,0,0.15), 0 4px 12px rgba(0,0,0,0.1)";
                e.currentTarget.style.background = "rgba(255,255,255,1)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 8px 32px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.05)";
                e.currentTarget.style.background = "rgba(255,255,255,0.95)";
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 5C3 3.89543 3.89543 3 5 3H8.27924C8.70967 3 9.09181 3.27543 9.22792 3.68377L10.7257 8.17721C10.8831 8.64932 10.6694 9.16531 10.2243 9.38787L7.96701 10.5165C9.06925 12.9612 11.0388 14.9308 13.4835 16.033L14.6121 13.7757C14.8347 13.3306 15.3507 13.1169 15.8228 13.2743L20.3162 14.7721C20.7246 14.9082 21 15.2903 21 15.7208V19C21 20.1046 20.1046 21 19 21H18C9.71573 21 3 14.2843 3 6V5Z"
                    fill="white"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span style={{ fontSize: 16, fontWeight: 600, color: "#333" }}>
                +234 905 405 6702
              </span>
            </a>
            <a
              href="mailto:info@eventnova.com.ng"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "14px",
                background: "rgba(255,255,255,0.95)",
                padding: "16px 28px",
                borderRadius: "16px",
                backdropFilter: "blur(20px)",
                border: "2px solid rgba(255,255,255,0.3)",
                boxShadow:
                  "0 8px 32px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.05)",
                textDecoration: "none",
                color: "#333",
                fontWeight: 600,
                fontSize: 16,
                transition: "all 0.3s ease",
                cursor: "pointer",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow =
                  "0 12px 40px rgba(0,0,0,0.15), 0 4px 12px rgba(0,0,0,0.1)";
                e.currentTarget.style.background = "rgba(255,255,255,1)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 8px 32px rgba(0,0,0,0.1), 0 2px 8px rgba(0,0,0,0.05)";
                e.currentTarget.style.background = "rgba(255,255,255,0.95)";
              }}
            >
              <div
                style={{
                  width: 48,
                  height: 48,
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19Z"
                    fill="white"
                    stroke="white"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
              <span style={{ fontSize: 16, fontWeight: 600, color: "#333" }}>
                info@eventnova.com.ng
              </span>
            </a>
          </div>
        </div>

        <style>{`
          @keyframes float {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-20px); }
          }
          @keyframes bounce {
            0%, 100% { transform: translateY(0px); }
            50% { transform: translateY(-10px); }
          }
          @keyframes rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes wave {
            0%, 100% { transform: translateX(0px); }
            50% { transform: translateX(-15px); }
          }
          @media (max-width: 768px) {
            .contact-quick-info {
              flex-direction: column !important;
              gap: 16px !important;
              margin-top: 32px !important;
            }
            .contact-quick-info a {
              width: 100% !important;
              max-width: 320px !important;
              padding: 14px 24px !important;
              font-size: 15px !important;
            }
            .contact-quick-info a div {
              width: 44px !important;
              height: 44px !important;
            }
            .contact-quick-info a div svg {
              width: 20px !important;
              height: 20px !important;
            }
          }
        `}</style>
      </section>

      {/* Contact Information & Form Section */}
      <section style={{ padding: "80px 0", background: "#f8f9fa" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 20px" }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 60,
              alignItems: "start",
            }}
          >
            {/* Contact Information */}
            <div>
              <h2
                style={{
                  fontSize: 36,
                  fontWeight: 700,
                  marginBottom: 40,
                  color: "#333",
                }}
              >
                Contact Information
              </h2>

              <div style={{ marginBottom: 40 }}>
                <h3
                  style={{
                    fontSize: 24,
                    fontWeight: 600,
                    marginBottom: 20,
                    color: "#667eea",
                  }}
                >
                  Office Address
                </h3>
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      borderRadius: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: 20,
                      flexShrink: 0,
                      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                    }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z"
                        fill="white"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle
                        cx="12"
                        cy="10"
                        r="3"
                        fill="#667eea"
                        stroke="#667eea"
                        strokeWidth="1.5"
                      />
                    </svg>
                  </div>
                  <div>
                    <p
                      style={{
                        fontSize: 16,
                        lineHeight: 1.7,
                        color: "#333",
                        margin: 0,
                        fontWeight: 500,
                      }}
                    >
                      HSE 5 AKPERE ORSHI STREET W/COL, Gwarimpa
                      <br />
                      Abuja, Nigeria
                      <br />
                      <span style={{ color: "#667eea", fontWeight: 600 }}>
                        Postal Code:
                      </span>{" "}
                      900108
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: 40 }}>
                <h3
                  style={{
                    fontSize: 24,
                    fontWeight: 600,
                    marginBottom: 20,
                    color: "#667eea",
                  }}
                >
                  Contact Details
                </h3>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      borderRadius: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: 20,
                      flexShrink: 0,
                      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                    }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 5C3 3.89543 3.89543 3 5 3H8.27924C8.70967 3 9.09181 3.27543 9.22792 3.68377L10.7257 8.17721C10.8831 8.64932 10.6694 9.16531 10.2243 9.38787L7.96701 10.5165C9.06925 12.9612 11.0388 14.9308 13.4835 16.033L14.6121 13.7757C14.8347 13.3306 15.3507 13.1169 15.8228 13.2743L20.3162 14.7721C20.7246 14.9082 21 15.2903 21 15.7208V19C21 20.1046 20.1046 21 19 21H18C9.71573 21 3 14.2843 3 6V5Z"
                        fill="white"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <p
                      style={{
                        fontSize: 16,
                        color: "#333",
                        margin: 0,
                        fontWeight: 500,
                      }}
                    >
                      <strong style={{ color: "#222", fontWeight: 600 }}>
                        Phone:
                      </strong>{" "}
                      <a
                        href="tel:+2349054056702"
                        style={{
                          color: "#667eea",
                          textDecoration: "none",
                          fontWeight: 500,
                        }}
                      >
                        +234 905 405 6702
                      </a>
                    </p>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      borderRadius: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: 20,
                      flexShrink: 0,
                      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                    }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M3 8L10.89 13.26C11.2187 13.4793 11.6049 13.5963 12 13.5963C12.3951 13.5963 12.7813 13.4793 13.11 13.26L21 8M5 19H19C19.5304 19 20.0391 18.7893 20.4142 18.4142C20.7893 18.0391 21 17.5304 21 17V7C21 6.46957 20.7893 5.96086 20.4142 5.58579C20.0391 5.21071 19.5304 5 19 5H5C4.46957 5 3.96086 5.21071 3.58579 5.58579C3.21071 5.96086 3 6.46957 3 7V17C3 17.5304 3.21071 18.0391 3.58579 18.4142C3.96086 18.7893 4.46957 19 5 19Z"
                        fill="white"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <p
                      style={{
                        fontSize: 16,
                        color: "#333",
                        margin: 0,
                        fontWeight: 500,
                      }}
                    >
                      <strong style={{ color: "#222", fontWeight: 600 }}>
                        Email:
                      </strong>{" "}
                      <a
                        href="mailto:info@eventnova.com.ng"
                        style={{
                          color: "#667eea",
                          textDecoration: "none",
                          fontWeight: 500,
                        }}
                      >
                        info@eventnova.com.ng
                      </a>
                    </p>
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      background:
                        "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                      borderRadius: "14px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginRight: 20,
                      flexShrink: 0,
                      boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)",
                    }}
                  >
                    <svg
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
                        fill="white"
                      />
                      <path
                        d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M2 12H22M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                  <div>
                    <p
                      style={{
                        fontSize: 16,
                        color: "#333",
                        margin: 0,
                        fontWeight: 500,
                      }}
                    >
                      <strong style={{ color: "#222", fontWeight: 600 }}>
                        Website:
                      </strong>{" "}
                      <a
                        href="https://www.eventnova.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: "#667eea",
                          textDecoration: "none",
                          fontWeight: 500,
                        }}
                      >
                        www.eventnova.com
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <h3
                  style={{
                    fontSize: 24,
                    fontWeight: 600,
                    marginBottom: 20,
                    color: "#667eea",
                  }}
                >
                  Business Hours
                </h3>
                <div
                  style={{
                    background: "white",
                    padding: 20,
                    borderRadius: 12,
                    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
                  }}
                >
                  <p
                    style={{
                      fontSize: 16,
                      color: "#333",
                      margin: "8px 0",
                      fontWeight: 500,
                    }}
                  >
                    <strong style={{ color: "#222", fontWeight: 600 }}>
                      Monday - Friday:
                    </strong>{" "}
                    8:00 AM - 6:00 PM
                  </p>
                  <p
                    style={{
                      fontSize: 16,
                      color: "#333",
                      margin: "8px 0",
                      fontWeight: 500,
                    }}
                  >
                    <strong style={{ color: "#222", fontWeight: 600 }}>
                      Saturday:
                    </strong>{" "}
                    9:00 AM - 4:00 PM
                  </p>
                  <p
                    style={{
                      fontSize: 16,
                      color: "#333",
                      margin: "8px 0",
                      fontWeight: 500,
                    }}
                  >
                    <strong style={{ color: "#222", fontWeight: 600 }}>
                      Sunday:
                    </strong>{" "}
                    Closed
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div
              style={{
                background: "white",
                padding: 40,
                borderRadius: 16,
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              }}
            >
              <h2
                style={{
                  fontSize: 36,
                  fontWeight: 700,
                  marginBottom: 30,
                  color: "#333",
                }}
              >
                Send Us a Message
              </h2>

              {/* Success/Error Messages */}
              {submitStatus === "success" && (
                <div
                  style={{
                    background: "#d4edda",
                    color: "#155724",
                    padding: "12px 16px",
                    borderRadius: 8,
                    marginBottom: 20,
                    border: "1px solid #c3e6cb",
                  }}
                >
                  Thank you for your message! We will get back to you soon.
                </div>
              )}

              {submitStatus === "error" && (
                <div
                  style={{
                    background: "#f8d7da",
                    color: "#721c24",
                    padding: "12px 16px",
                    borderRadius: 8,
                    marginBottom: 20,
                    border: "1px solid #f5c6cb",
                  }}
                >
                  Sorry, there was an error sending your message. Please try
                  again.
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 20 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 8,
                      fontWeight: 600,
                      color: "#333",
                    }}
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "2px solid #e1e5e9",
                      borderRadius: 8,
                      fontSize: 16,
                      transition: "border-color 0.3s ease",
                      opacity: isSubmitting ? 0.6 : 1,
                    }}
                    placeholder="Enter your full name"
                  />
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 8,
                      fontWeight: 600,
                      color: "#333",
                    }}
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "2px solid #e1e5e9",
                      borderRadius: 8,
                      fontSize: 16,
                      transition: "border-color 0.3s ease",
                      opacity: isSubmitting ? 0.6 : 1,
                    }}
                    placeholder="Enter your email address"
                  />
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 8,
                      fontWeight: 600,
                      color: "#333",
                    }}
                  >
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "2px solid #e1e5e9",
                      borderRadius: 8,
                      fontSize: 16,
                      transition: "border-color 0.3s ease",
                      opacity: isSubmitting ? 0.6 : 1,
                    }}
                    placeholder="Enter your phone number"
                  />
                </div>

                <div style={{ marginBottom: 20 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 8,
                      fontWeight: 600,
                      color: "#333",
                    }}
                  >
                    Subject *
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "2px solid #e1e5e9",
                      borderRadius: 8,
                      fontSize: 16,
                      transition: "border-color 0.3s ease",
                      background: "white",
                      opacity: isSubmitting ? 0.6 : 1,
                    }}
                  >
                    <option value="">Select a subject</option>
                    <option value="general">General Inquiry</option>
                    <option value="support">Customer Support</option>
                    <option value="partnership">Partnership</option>
                    <option value="feedback">Feedback</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div style={{ marginBottom: 30 }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: 8,
                      fontWeight: 600,
                      color: "#333",
                    }}
                  >
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    disabled={isSubmitting}
                    style={{
                      width: "100%",
                      padding: "12px 16px",
                      border: "2px solid #e1e5e9",
                      borderRadius: 8,
                      fontSize: 16,
                      transition: "border-color 0.3s ease",
                      resize: "vertical",
                      opacity: isSubmitting ? 0.6 : 1,
                    }}
                    placeholder="Enter your message here..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    width: "100%",
                    padding: "16px 24px",
                    background: isSubmitting
                      ? "#ccc"
                      : "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                    color: "white",
                    border: "none",
                    borderRadius: 8,
                    fontSize: 18,
                    fontWeight: 600,
                    cursor: isSubmitting ? "not-allowed" : "pointer",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    opacity: isSubmitting ? 0.7 : 1,
                  }}
                  onMouseOver={(e) => {
                    if (!isSubmitting) {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 6px 20px rgba(102, 126, 234, 0.4)";
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!isSubmitting) {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }
                  }}
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </button>
              </form>
              <div
                style={{
                  marginTop: 32,
                  padding: 24,
                  borderRadius: 16,
                  background: "#eef2ff",
                  border: "1px solid #c7d2fe",
                  textAlign: "center",
                }}
              >
                <h3
                  style={{
                    margin: "0 0 8px",
                    fontSize: 22,
                    fontWeight: 700,
                    color: "#312e81",
                  }}
                >
                  Already a client?
                </h3>
                <p
                  style={{
                    margin: "0 0 12px",
                    color: "#4338ca",
                    fontWeight: 500,
                  }}
                >
                  Manage tickets or open new cases in our Support Center.
                </p>
                <a
                  href="/support"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "12px 24px",
                    borderRadius: 999,
                    fontWeight: 700,
                    color: "#fff",
                    background:
                      "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                    textDecoration: "none",
                  }}
                >
                  Visit Support Center
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section style={{ padding: "60px 0", background: "white" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 20px" }}>
          <h2
            style={{
              fontSize: 36,
              fontWeight: 700,
              marginBottom: 40,
              textAlign: "center",
              color: "#333",
            }}
          >
            Find Us on the Map
          </h2>

          <div
            style={{
              width: "100%",
              height: 500,
              borderRadius: 16,
              overflow: "hidden",
              boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
              border: "1px solid #e1e5e9",
            }}
          >
            <iframe
              src="https://www.google.com/maps?q=Gwarimpa+Abuja+Nigeria&output=embed"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="EventNova Office Location - Gwarimpa, Abuja"
            />
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ padding: "80px 0", background: "#f8f9fa" }}>
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 20px" }}>
          <h2
            style={{
              fontSize: 36,
              fontWeight: 700,
              marginBottom: 50,
              textAlign: "center",
              color: "#333",
            }}
          >
            Frequently Asked Questions
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(500px, 1fr))",
              gap: 30,
            }}
          >
            <div
              style={{
                background: "white",
                padding: 30,
                borderRadius: 12,
                boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
              }}
            >
              <h3
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  marginBottom: 15,
                  color: "#667eea",
                }}
              >
                How do I engage with vendors through EventNova?
              </h3>
              <p
                style={{
                  fontSize: 16,
                  color: "#666",
                  lineHeight: 1.6,
                  margin: 0,
                }}
              >
                EventNova is a showcase platform where vendors subscribe to
                display their services to potential clients. Browse our vendor
                directory, review their profiles and portfolios, then contact
                them directly to discuss your event needs and arrange your
                business outside the platform.
              </p>
            </div>

            <div
              style={{
                background: "white",
                padding: 30,
                borderRadius: 12,
                boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
              }}
            >
              <h3
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  marginBottom: 15,
                  color: "#667eea",
                }}
              >
                What types of events do you support?
              </h3>
              <p
                style={{
                  fontSize: 16,
                  color: "#666",
                  lineHeight: 1.6,
                  margin: 0,
                }}
              >
                We support all types of events including weddings, birthdays,
                corporate events, conferences, and special celebrations. Our
                vendors cover every aspect of event planning.
              </p>
            </div>

            <div
              style={{
                background: "white",
                padding: 30,
                borderRadius: 12,
                boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
              }}
            >
              <h3
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  marginBottom: 15,
                  color: "#667eea",
                }}
              >
                How do I become a vendor on EventNova?
              </h3>
              <p
                style={{
                  fontSize: 16,
                  color: "#666",
                  lineHeight: 1.6,
                  margin: 0,
                }}
              >
                To become a vendor, register on our platform, complete your
                profile, and submit required documentation. Our team will review
                and approve your application within 48 hours.
              </p>
            </div>

            <div
              style={{
                background: "white",
                padding: 30,
                borderRadius: 12,
                boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
              }}
            >
              <h3
                style={{
                  fontSize: 20,
                  fontWeight: 600,
                  marginBottom: 15,
                  color: "#667eea",
                }}
              >
                What is your cancellation policy?
              </h3>
              <p
                style={{
                  fontSize: 16,
                  color: "#666",
                  lineHeight: 1.6,
                  margin: 0,
                }}
              >
                Cancellation policies vary by vendor. Most vendors allow
                cancellations up to 48 hours before the event. Please check
                individual vendor policies when booking.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
